import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { getFirebaseClients } from './firebase';

export type UploadKind = 'image' | 'pdf' | 'video';

const MAX_BYTES: Record<UploadKind, number> = {
  image: 5 * 1024 * 1024,
  pdf: 10 * 1024 * 1024,
  // 50MB cap — mirrors the `isVideo()` size check in storage.rules. Short
  // clips only; longer recordings should go to YouTube/Vimeo.
  video: 50 * 1024 * 1024,
};

function extOf(file: File): string {
  const fromName = file.name.split('.').pop();
  if (fromName) return fromName.toLowerCase();
  if (file.type === 'application/pdf') return 'pdf';
  if (file.type.startsWith('image/')) return file.type.split('/').pop() ?? 'bin';
  if (file.type.startsWith('video/')) {
    // video/quicktime → mov; video/mp4 → mp4; etc.
    const sub = file.type.split('/').pop() ?? 'bin';
    return sub === 'quicktime' ? 'mov' : sub;
  }
  return 'bin';
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image decode failed'));
    img.src = src;
  });
}

/**
 * Convert a raster image to WebP (and downscale so the longest side is at most
 * `maxDim`px) entirely in the browser, before upload. This keeps Storage light
 * and pages fast — a 6 MB phone JPEG typically becomes a ~150–400 KB WebP.
 *
 * Untouched: SVG (vector) and GIF (may be animated — canvas would flatten it).
 * Any failure falls back to the original file, so an upload never breaks.
 */
async function toWebp(file: File, maxDim = 1920, quality = 0.82): Promise<File> {
  if (file.type === 'image/svg+xml' || file.type === 'image/gif') return file;
  if (typeof document === 'undefined') return file; // non-browser safety
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const longest = Math.max(img.width, img.height) || 1;
    const scale = Math.min(1, maxDim / longest);
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((res) =>
      canvas.toBlob(res, 'image/webp', quality),
    );
    if (!blob || blob.type !== 'image/webp') return file; // browser lacks WebP encode
    const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
    return new File([blob], `${baseName}.webp`, { type: 'image/webp' });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Upload a file to Storage under `pathPrefix/<uuid>.<ext>` and return the
 * download URL. Mirrors the server-side rule constraints (size + content type)
 * to fail fast on the client.
 */
export async function uploadFile({
  file,
  pathPrefix,
  kind,
}: {
  file: File;
  pathPrefix: string;
  kind: UploadKind;
}): Promise<string> {
  // Validate the ORIGINAL file's kind first (clear error messages).
  if (kind === 'image' && !file.type.startsWith('image/')) {
    throw new Error('Format non supporté (image attendue).');
  }
  if (kind === 'pdf' && file.type !== 'application/pdf') {
    throw new Error('Fichier non supporté (PDF attendu).');
  }
  if (kind === 'video' && !file.type.startsWith('video/')) {
    throw new Error('Format non supporté (vidéo attendue : mp4 ou mov).');
  }

  // Images are converted to WebP + downscaled in the browser before upload, so
  // we size-check the CONVERTED file (a large original is fine if it shrinks
  // under the limit). Non-images are uploaded as-is.
  const toUpload = kind === 'image' ? await toWebp(file) : file;

  if (toUpload.size > MAX_BYTES[kind]) {
    throw new Error(
      `Fichier trop volumineux (max ${MAX_BYTES[kind] / 1024 / 1024} Mo).`,
    );
  }

  const { storage } = getFirebaseClients();
  const id = crypto.randomUUID();
  const path = `${pathPrefix.replace(/\/$/, '')}/${id}.${extOf(toUpload)}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, toUpload, { contentType: toUpload.type });
  return getDownloadURL(storageRef);
}

/** Best-effort delete of a previously uploaded file by its download URL. */
export async function deleteFileByUrl(url: string): Promise<void> {
  const { storage } = getFirebaseClients();
  try {
    const storageRef = ref(storage, url);
    await deleteObject(storageRef);
  } catch {
    // Orphan files are acceptable for now; surfacing this would only confuse admins.
  }
}
